/**
 * **신전 — 생명의 돌을 바치는 자리** (CYCLE-R2 §4, DESIGN 14.53).
 *
 * 이 세계는 정해진 시각에 끝난다. 보스가 떨구는 **생명의 돌**을 바치면 그만큼
 * 더 산다. 그래서 이 화면이 답해야 하는 것은 셋뿐이다 —
 * *지금 얼마나 남았나 · 내가 뭘 할 수 있나 · 하면 얼마나 늘어나나.*
 *
 * ⚠ **거절을 성공처럼 보이면 안 된다.** 서버는 세계가 닫혔거나 D1 이 실패하면
 * **돌을 안 쓰고** 사유를 문장으로 보낸다(`town.ts` `offerStone`). 그래서 화면은
 * 결과를 **미리 그리지 않는다** — 누르면 서버 문장이 로그로 오고, 다음 `you`/`world`
 * 로 숫자가 바뀐다. 먼저 "바쳐졌다" 를 그려두면 거절된 순간 화면이 거짓말이 된다.
 *
 * ⚠ **버튼을 미리 회색으로 막지 않는다** — 돌이 없을 때만 뺀다. 종료 경합은
 * 화면이 모른다(서버만 안다). 모르는 것을 아는 척 막으면 되는 일을 막는 쪽으로
 * 틀린다(`places.js` 머리말의 ⓐ). 남은 판단은 서버 문장이 한다.
 */
import { S, esc } from '../../core/state.js';
import { send } from '../../core/net.js';
import { currentDoomStage, worldClockNow } from '../../render/doom-stage.js';
import { LIFE_STONE_KEY, LIFE_STONE_SECS } from '../../world/cycle.js';
import { TEMPLE_RING, gradeOf } from '../../world/map.js';
import { buyBtn, fold, muted, num, sec, split, npcHead } from './common.js';
import { lifeStoneChoices, awakenRow } from './inv.js';

let ranksLoading = false, ranksFailed = false, ranksTimer;

export function requestRanks(redraw) {
  clearTimeout(ranksTimer);
  ranksFailed = !S.ws || S.ws.readyState !== 1;
  ranksLoading = !ranksFailed;
  if (ranksLoading) {
    send({ t: 'contributionRanks' });
    ranksTimer = setTimeout(() => { ranksLoading = false; ranksFailed = true; redraw(); }, 10000);
  }
  redraw();
}

export function ranksReceived() {
  clearTimeout(ranksTimer);
  ranksLoading = false; ranksFailed = false;
}

function rankingSections() {
  const d = S.dex?.contributionRanks;
  const retry = '<button class="ghost" onclick="act.contributionRanks()">다시 불러오기</button>';
  let message = '';
  if (ranksLoading) message = '봉헌 순위를 불러오는 중…';
  else if (ranksFailed || d?.status === 'error') message = '봉헌 순위를 불러오지 못했다. 연결 상태를 확인하고 다시 불러와 달라.';
  else if (!d || d.world !== S.world?.n) message = '현재 세계의 봉헌 순위를 아직 받지 못했다. 다시 불러와 달라.';
  if (message) return sec('생명의 돌 기부자', `<p class="muted" role="status">${message}</p>` + (ranksLoading ? '' : retry));
  const rows = (list) => list.length
    ? list.map(r => `<div class="kv"><span><i class="qt">${num(r.rank)}</i> ${esc(r.name)}</span><b>${num(r.stones)}개 <i class="qt">· ${span(r.seconds)}</i></b></div>`).join('')
    : muted('아직 봉헌 기록이 없다. 생명의 돌을 바치면 이름이 남는다.');
  return sec('역대 생명의 돌 기부자', muted('모든 세계의 봉헌 개수 합계 · 상위 100명') + rows(d.allTime))
    + sec(`현재 세계 기부자 <em>제 ${num(d.world)} 세계</em>`, rows(d.currentWorld)
      + muted('상위 100명 · 같은 개수면 먼저 봉헌한 순서다. 이름은 마지막 봉헌 당시의 이름이다.') + retry);
}

/** 초 → "3시간 20분" · "40분". 시계(hud)는 남은 시간을, 여기는 **늘어나는 양**을 말한다 */
function span(secs) {
  const minutes = Math.max(0, Math.round(secs / 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return (h ? `${num(h)}시간` : '') + (m ? `${h ? ' ' : ''}${num(m)}분` : (h ? '' : '0분'));
}

/** Only a packet for this world/deadline can establish its current offering phase. */
function offeringState() {
  const w = S.world, stage = currentDoomStage(), now = worldClockNow();
  const known = w?.n > 0 && Number.isFinite(w.endsAt) && w.endsAt > 0;
  const left = known ? Math.max(0, w.endsAt - now) : null;
  if (!stage || !known || !(stage.finalDayAt > 0)) return { left, status: 'unknown', text: '봉헌 가능 시간을 확인 중이다. 마지막 날에는 생명의 돌을 바칠 수 없다.' };
  if (now >= w.endsAt) return { left, status: 'ended', text: '세계 종료 시각이 지났다. 세계 전환 중에는 봉헌할 수 없다.' };
  if (stage.finalDay || stage.phase === 3 || now >= stage.finalDayAt) return { left, status: 'final', text: '마지막 날 · 봉헌 마감. 생명의 돌은 소모되지 않는다.' };
  return { left, status: 'open', text: '봉헌 가능 · 마지막 날이 시작되기 전까지 생명의 돌을 바칠 수 있다.' };
}

/** Existing panel updates use this key; no independent countdown or timer is started. */
export function stateKey() {
  const state = offeringState();
  return `${S.world?.n ?? ''}:${S.world?.startedAt ?? ''}:${S.world?.endsAt ?? ''}:${S.world?.extendedBy ?? ''}:${state.status}:${state.left === null ? '' : Math.ceil(state.left / 60)}:${ranksLoading}:${ranksFailed}:${JSON.stringify(S.dex?.contributionRanks ?? null)}`;
}

/** Actual world ledger only. The first world's aligned deadline is not an offering extension. */
export function lifetimeRows(w, now = worldClockNow()) {
  if (!w || !(w.n > 0) || !Number.isFinite(w.startedAt) || !(w.startedAt > 0) || !Number.isFinite(w.endsAt)) return '';
  const when = (at) => new Date(at * 1000).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  return `<div class="kv"><span>시작 · 한국시간</span><b>${when(w.startedAt)}</b></div>`
    + `<div class="kv"><span>종료 예정 · 한국시간</span><b>${when(w.endsAt)}</b></div>`
    + `<div class="kv"><span>지나간 시간 / 예정 수명</span><b>${span(Math.max(0, now - w.startedAt))} / ${span(Math.max(0, w.endsAt - w.startedAt))}</b></div>`
    + `<div class="kv"><span>봉헌으로 늘어난 시간</span><b>${Number.isFinite(w.extendedBy) && w.extendedBy >= 0 ? span(w.extendedBy) : '봉헌 기록을 확인 중이다'}</b></div>`;
}

/**
 * Q276 — 메뉴 › 세계 › **세계 수명**. 신전 창의 위쪽(이 세계가 얼마나 남았나 · 봉헌이 얼마나 늘렸나)과 봉헌 순위를
 * 어디서나 읽는다. 바치기·반지 요청은 신전 곁에서만 되므로 여기에는 버튼을 두지 않는다(마을 신전 NPC 로 여는 길은 그대로).
 */
export function worldLifeSections(showClock = true) {
  const w = S.world, offering = offeringState(), left = offering.left;
  const clock = left !== null
    ? `<div class="ux-fact ux-primary"><span>제 ${num(w.n)} 세계 · 남은 시간</span><b class="ux-number">${span(left)}</b></div>`
    : muted('<span class="ldspin" aria-hidden="true"></span>남은 시간 불러오는 중…');
  return sec('생명의 돌 봉헌', (showClock ? clock : '')
    + `<div class="ux-facts"><div class="ux-fact"><span>생명의 돌 1개당 연장</span><b class="ux-number">${span(LIFE_STONE_SECS)}</b></div></div>`
    + `<p role="status" data-offering-state="${offering.status}">${offering.text}</p>`
    + muted('생명의 돌은 마을 <b>신전</b>에서 바친다 — 마지막 날이 시작되기 전까지.'))
    + fold('세계의 시작·종료 시각과 봉헌 기록', lifetimeRows(w)
      + '<div class="kv"><span>누적 연장 상한</span><b>없음</b></div>' + rankingSections());
}

export function render(ctx) {
  const { y, at, j } = ctx;
  const have = j[LIFE_STONE_KEY] || 0;
  const w = S.world;
  const offering = offeringState();

  // ── 위: 지금 세계가 얼마나 남았나 ──
  const left = offering.left;
  const clock = left !== null
    ? `<div class="ux-fact ux-primary"><span>제 ${num(w.n)} 세계 · 남은 시간</span><b class="ux-number">${span(left)}</b></div>`
    : muted('<span class="ldspin" aria-hidden="true"></span>남은 시간 불러오는 중…');

  const focus = npcHead() + sec('이 세계', clock
    + `<div class="ux-facts"><div class="ux-fact"><span>돌 1개를 바치면</span><b class="ux-number">+${span(LIFE_STONE_SECS)}</b></div></div>`)
    + sec('바치기',
      `<div class="ux-facts"><div class="ux-fact"><span>가진 생명의 돌</span><b><span class="ux-number">${num(have)}</span><small>개</small></b></div></div>`
      + `<p class="muted${offering.status === 'final' || offering.status === 'ended' ? ' w' : ''}" role="status" data-offering-state="${offering.status}">${offering.text}</p>`
      + '<div class="row">'
      + buyBtn('생명의 돌 바치기', 'act.offer()', {
        y, at, need: 'offer',
        more: have > 0 ? [] : ['생명의 돌 없음'],
      })
      + '</div>'
      // 2026-10-21 — 「마지막 날 전까지」는 바로 위 상태 줄(offering.text)이 이미 말한다. 여기는 남은 한 가지만
      + muted(have > 0
        ? '마감과 겹치거나 저장에 실패해도 돌은 그대로 남는다'
        : '생명의 돌은 보스 처치·탑 보상으로 얻는다'));

  /**
   * UNI-Q36 — **사제의 반지.** 재료·종용 횟수·켬끔은 서버 설정값(`you.templeRing.cost`)를 그대로 적는다.
   * 능력치는 **받은 이에게만** 서버가 보낸다(`base`) — 받기 전에는 「비공개」로 그린다. 화면은 산식을 갖지 않는다.
   * 사제는 몇 번 물리친다 — 횟수(`plea`)를 적어 주어 「눌러도 아무 일도 안 난다」로 읽히지 않게 한다.
   */
  const tr = y.templeRing;
  const cost = tr?.cost;
  const lack = [];
  if (cost && cost.on === false) lack.push('지금은 받지 않는다');
  if (cost && have < cost.stones) lack.push(`생명의 돌 ${num(cost.stones - have)} 부족`);
  const ring = sec('사제의 반지',
    `<div class="kv"><span>${TEMPLE_RING.name}</span><b>${gradeOf(TEMPLE_RING.grade).name} 반지</b></div>`
    + (cost
      ? `<div class="kv"><span>교환 재료</span><b>생명의 돌 ${num(cost.stones)}개 · 은화 ${num(cost.silver)}</b></div>`
      : muted('교환 조건을 확인 중이다.'))
    + `<div class="kv"><span>능력치</span><b>${tr?.revealed && Number.isFinite(tr.base) ? `모든 능력치 +${num(tr.base)} (신화 +0)` : '반지 획득 후 공개'}</b></div>`
    + (Number(tr?.plea) > 0
      ? `<p class="muted" role="status" data-temple-plea="${num(tr.plea)}">반지 요청 ${num(tr.plea)}회. 사제에게 다시 요청해 달라.</p>`
      : muted('사제는 여러 번 청해야 반지를 내준다'))
    + '<div class="row">'
    + buyBtn('반지 요청', 'act.templeRing()', { y, at, need: 'templeRing', price: cost ? cost.silver : 0, more: lack })
    + '</div>'
    + muted('+10 둘을 합치면 궁극 · 팔기·분해 불가 · 죽어도 남는다'));

  // ── 아래: 왜 이런 것이 있는가 ──
  const list = sec('생명의 돌의 세 선택', lifeStoneChoices(y) + awakenRow(y, at))
    + ring + fold('세계 수명과 생명의 돌 기부자', lifetimeRows(w)
      + '<div class="kv"><span>누적 연장 상한</span><b>없음</b></div>' + rankingSections()) + sec('신전', muted(
      '바치면 세계 종료가 늦춰지고 이름이 <b>전체 공지</b>에 뜬다 · 지난 세계의 순위·기록은 보관된다'));

  return split(focus, list);
}
